/**
 * Shell Layout Component
 * Phase 68 M4: Header + BreadcrumbBar + SecondarySidebar + Content
 *
 * Routes map to shell config (breadcrumbs + sidebar) via useShellConfig hook.
 * AppSidebar is overlay-only, triggered by hamburger in Header.
 */

import React, { useState, useMemo, useCallback } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Box, Drawer, useTheme } from '@mui/material'
import Work from '@mui/icons-material/Work'
import Settings from '@mui/icons-material/Settings'
import AccountBalance from '@mui/icons-material/AccountBalance'

// BUG-020 fix: Compass SVG icon per prototype.html
const CompassIcon: React.FC<{ size?: number }> = ({ size = 15 }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    width={size}
    height={size}
  >
    <circle cx="12" cy="12" r="10" />
    <path d="M16.24 7.76l-2.12 6.36-6.36 2.12 2.12-6.36 6.36-2.12z" />
  </svg>
)

import AppSidebar from './AppSidebar'
import Header from './Header'
import BreadcrumbBar, { type Breadcrumb } from './BreadcrumbBar'
import SecondarySidebar from './SecondarySidebar'
import {
  CANTERA_SIDEBAR,
  COMPAS_SIDEBAR,
  ADMIN_SIDEBAR,
  BANRURAL_SIDEBAR,
  type SidebarConfig,
} from '@/config/sidebarConfigs'

// =============================================================================
// Shell Config Hook
// =============================================================================

interface ShellConfig {
  breadcrumbs: Breadcrumb[]
  sidebarConfig: SidebarConfig | null
}

function useShellConfig(): ShellConfig {
  const location = useLocation()
  const path = location.pathname

  return useMemo(() => {
    // Login — standalone, no shell chrome
    if (path === '/login') {
      return { breadcrumbs: [], sidebarConfig: null }
    }

    // Home — no breadcrumbs, no sidebar
    if (path === '/') {
      return { breadcrumbs: [], sidebarConfig: null }
    }

    // Chat — breadcrumbs, NO sidebar (per 001 Q2)
    if (path === '/chat' || path.startsWith('/chat/')) {
      return {
        breadcrumbs: [
          { label: 'Compás', href: '/chat', icon: <CompassIcon /> },
          { label: 'Chat' },
        ],
        sidebarConfig: null,
      }
    }

    // Knowledge — Compás sidebar (L2/L3)
    if (path === '/knowledge') {
      return {
        breadcrumbs: [
          { label: 'Compás', href: '/chat', icon: <CompassIcon /> },
          { label: 'Gestión de Documentos' },
        ],
        sidebarConfig: COMPAS_SIDEBAR,
      }
    }

    // User Management — Admin sidebar
    if (path === '/admin/users') {
      return {
        breadcrumbs: [
          { label: 'Administracion', href: '/admin/users', icon: <Settings sx={{ fontSize: 16 }} /> },
          { label: 'Gestion de Usuarios' },
        ],
        sidebarConfig: ADMIN_SIDEBAR,
      }
    }

    // Platform Dashboard — Admin sidebar (Phase 68 M5)
    if (path === '/admin/plataforma') {
      return {
        breadcrumbs: [
          { label: 'Administracion', href: '/admin/users', icon: <Settings sx={{ fontSize: 16 }} /> },
          { label: 'Plataforma' },
        ],
        sidebarConfig: ADMIN_SIDEBAR,
      }
    }

    // Permissions — Admin sidebar (Phase 70)
    if (path === '/admin/permisos') {
      return {
        breadcrumbs: [
          { label: 'Administracion', href: '/admin/users', icon: <Settings sx={{ fontSize: 16 }} /> },
          { label: 'Permisos' },
        ],
        sidebarConfig: ADMIN_SIDEBAR,
      }
    }

    // Profile — single-level breadcrumb, no sidebar
    if (path === '/profile') {
      return {
        breadcrumbs: [{ label: 'Configuracion' }],
        sidebarConfig: null,
      }
    }

    // Cantera section — all get Cantera sidebar
    if (path.startsWith('/talent-pipeline')) {
      const canteraIcon = <Work sx={{ fontSize: 16 }} />
      const base: Breadcrumb = { label: 'Cantera', href: '/talent-pipeline', icon: canteraIcon }

      if (path === '/talent-pipeline') {
        return {
          breadcrumbs: [base, { label: 'Buscar Candidatos' }],
          sidebarConfig: CANTERA_SIDEBAR,
        }
      }
      if (path === '/talent-pipeline/tablero') {
        return {
          breadcrumbs: [base, { label: 'Tablero' }],
          sidebarConfig: CANTERA_SIDEBAR,
        }
      }
      if (path === '/talent-pipeline/upload') {
        return {
          breadcrumbs: [base, { label: 'Admin de Talento' }],
          sidebarConfig: CANTERA_SIDEBAR,
        }
      }
      if (path === '/talent-pipeline/positions') {
        return {
          breadcrumbs: [base, { label: 'Gestion de Posiciones' }],
          sidebarConfig: CANTERA_SIDEBAR,
        }
      }
      if (path === '/talent-pipeline/projects') {
        return {
          breadcrumbs: [base, { label: 'Asignaciones' }],
          sidebarConfig: CANTERA_SIDEBAR,
        }
      }
      if (path.startsWith('/talent-pipeline/projects/')) {
        return {
          breadcrumbs: [
            base,
            { label: 'Asignaciones', href: '/talent-pipeline/projects' },
            { label: '...' }, // Page will override with project name
          ],
          sidebarConfig: CANTERA_SIDEBAR,
        }
      }
      if (path === '/talent-pipeline/clients') {
        return {
          breadcrumbs: [base, { label: 'Clientes' }],
          sidebarConfig: CANTERA_SIDEBAR,
        }
      }
      if (path.startsWith('/talent-pipeline/clients/')) {
        return {
          breadcrumbs: [
            base,
            { label: 'Clientes', href: '/talent-pipeline/clients' },
            { label: '...' }, // Page will override with client name
          ],
          sidebarConfig: CANTERA_SIDEBAR,
        }
      }
      // Phase 113 — Colaboradores (employees) roster + detail
      if (path === '/talent-pipeline/colaboradores') {
        return {
          breadcrumbs: [base, { label: 'Colaboradores' }],
          sidebarConfig: CANTERA_SIDEBAR,
        }
      }
      if (path.startsWith('/talent-pipeline/colaboradores/')) {
        return {
          breadcrumbs: [
            base,
            { label: 'Colaboradores', href: '/talent-pipeline/colaboradores' },
            { label: '...' }, // Page will override with employee name
          ],
          sidebarConfig: CANTERA_SIDEBAR,
        }
      }

      // Fallback for any other talent-pipeline routes
      return {
        breadcrumbs: [base],
        sidebarConfig: CANTERA_SIDEBAR,
      }
    }

    // Banrural section — all get Banrural sidebar
    if (path.startsWith('/banrural')) {
      const banruralIcon = <AccountBalance sx={{ fontSize: 16 }} />
      const base: Breadcrumb = { label: 'Banrural', href: '/banrural', icon: banruralIcon }

      if (path === '/banrural') {
        return {
          breadcrumbs: [base, { label: 'Dashboard' }],
          sidebarConfig: BANRURAL_SIDEBAR,
        }
      }
      if (path === '/banrural/extraccion') {
        return {
          breadcrumbs: [base, { label: 'Extracción de Datos' }],
          sidebarConfig: BANRURAL_SIDEBAR,
        }
      }
      if (path.startsWith('/banrural/analisis/')) {
        return {
          breadcrumbs: [base, { label: 'Análisis de Caso' }],
          sidebarConfig: BANRURAL_SIDEBAR,
        }
      }
      return {
        breadcrumbs: [base],
        sidebarConfig: BANRURAL_SIDEBAR,
      }
    }

    // Default: no shell extras
    return { breadcrumbs: [], sidebarConfig: null }
  }, [path])
}

// =============================================================================
// Layout Component
// =============================================================================

interface Props {
  children: React.ReactNode
}

const Layout: React.FC<Props> = ({ children }) => {
  const theme = useTheme()
  const navigate = useNavigate()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null)
  const { breadcrumbs, sidebarConfig } = useShellConfig()

  // BUG-027 fix: Close sidebar FIRST, navigate AFTER Drawer exit animation
  const handleSidebarNavigate = useCallback((path: string) => {
    setPendingNavigation(path)
    setSidebarOpen(false)  // Close triggers exit animation
  }, [])

  const handleDrawerExited = useCallback(() => {
    if (pendingNavigation) {
      navigate(pendingNavigation)
      setPendingNavigation(null)
    }
  }, [navigate, pendingNavigation])

  return (
    <Box
      id="layout-root"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100dvh',
        overflow: 'hidden',
        bgcolor: theme.palette.background.default,
      }}
    >
      {/* Overlay sidebar (AppSidebar as Drawer) */}
      {/* BUG-027 fix: TransitionProps.onExited ensures navigation happens AFTER close animation */}
      <Drawer
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        SlideProps={{ onExited: handleDrawerExited }}
        sx={{
          '& .MuiDrawer-paper': {
            width: 280,
            border: 'none',
          },
        }}
      >
        <AppSidebar
          onClose={() => setSidebarOpen(false)}
          onNavigate={handleSidebarNavigate}
        />
      </Drawer>

      {/* Header — always visible */}
      <Header onMenuClick={() => setSidebarOpen(true)} />

      {/* Breadcrumb bar — conditional */}
      <BreadcrumbBar breadcrumbs={breadcrumbs} />

      {/* Content area: optional sidebar + main content */}
      <Box
        id="layout-content-area"
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          overflow: 'hidden',
        }}
      >
        {/* Secondary sidebar — conditional */}
        {sidebarConfig && <SecondarySidebar config={sidebarConfig} />}

        {/* Main content */}
        <Box
          id="layout-main"
          component="main"
          sx={{
            flex: 1,
            minWidth: 0,
            minHeight: 0,
            height: '100%',
            overflow: 'hidden',
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  )
}

export default Layout
